import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardTemplate } from "../components/templates/DashboardTemplate";
import PatientSidebar from "../components/organisms/PatientSidebar";
import DashboardEmptyState from "../components/templates/DashboardTemplate/EmptyState";
import CommonHeader from "../components/organisms/CommonHeader";
import AddPatientModal from "../components/molecules/AddPatientModal";
import { log, LogLevel } from "utils/LogUtil";
import { EMPTY_PATIENT } from "common/models/types";
import { savePatient } from "api/patientService";
import { WaitingManager } from "components/organisms/CommonUI/WaitingPopup/WaitingManager";
import { ToastManager } from "components/organisms/CommonUI/ToastPopup/ToastManager";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchPatientList,
  fetchReviewHistory,
  fetchPinnedReviews,
  fetchAiReviewCount,
  fetchPinnedReviewCount,
  fetchRecentNhisRequests,
} from "store/middleware";
import { AppDispatch, RootState } from "store";
import { eventLog } from "utils/AmplitudeUtil";
import { withPageGuard } from "components/common/withPageGuard";
import { UserManualManager } from "components/organisms/CommonUI";
import { getManualVersion } from "components/organisms/CommonUI/UserManualPopup/UserManualModal";
import { AGREEMENT_VERSION, GUIDE_VERSION } from "common/constants";

const Button = (props: React.ButtonHTMLAttributes<HTMLButtonElement>) => (
  <button
    style={{
      background: "#1976d2",
      color: "#fff",
      border: "none",
      borderRadius: 6,
      padding: "8px 16px",
      fontSize: 15,
      fontWeight: 500,

      cursor: "pointer",
      marginLeft: 8,
    }}
    {...props}
  />
);

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.user);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  useEffect(() => {
    // 대시보드 진입 시 항상 최신 데이터 갱신 (3.1.1 참조)
    dispatch(fetchReviewHistory());
    dispatch(fetchPinnedReviews());
    dispatch(fetchPinnedReviewCount());
    dispatch(fetchAiReviewCount(undefined));
    dispatch(fetchRecentNhisRequests(2));
  }, [dispatch]);

  useEffect(() => {
    // 약관 동의 및 가이드 동의가 완료되고 Dashboard에 진입한 상태에서만 실행
    if (!user) return;
    if (user.agreementVersion !== AGREEMENT_VERSION) return; // 약관 동의 완료 후에만 실행
    if (user.guidedVersion !== GUIDE_VERSION) return; // 가이드 동의 완료 후에만 실행

    const currentManualVersion = getManualVersion();
    const shouldShowGuide =
      !user.manualVersion ||
      user.manualVersion < currentManualVersion;

    if (shouldShowGuide) {
      // UserManualManager API로 팝업 열기
      UserManualManager.show({
        baseVersion: user.manualVersion, // 내부에서 시작 페이지 계산
        shouldUpdateVersion: true,  // 자동 팝업: DB 업데이트
      });
    }
  }, [user?.agreementVersion, user?.guidedVersion, user?.manualVersion]);

  const handlePatientSelect = (patientId: number) => {
    navigate(`/patients/${patientId}`);
  };

  const handleReportSelect = (patientId: number, reportId: number) => {
    navigate(`/patients/${patientId}/reviews/${reportId}`);
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleCreatePatient = async (patientData: {
    birthYear: string;
    gender: string;
    memo: string;
  }) => {
    try {
      log(LogLevel.INFO, "handleCreatePatient", patientData);

      WaitingManager.show({
        message: "환자 정보를 저장하는 중입니다...",
      });

      // 출생일 계산 (출생년도의 1월 1일로 설정)
      const birthDate = new Date(parseInt(patientData.birthYear), 0, 1).toISOString();

      // 새 환자 객체 생성
      const newPatient = {
        ...EMPTY_PATIENT,
        birthDate: birthDate,
        gender: patientData.gender,
        memo: patientData.memo,
      };

      const response = await savePatient(newPatient);
      log(LogLevel.VERBOSE, "handleCreatePatient response", response);

      if (response.error) {
        throw new Error(response.message || "환자 정보를 저장하는 데 실패했습니다.");
      }

      const patientId = response.data?.id;
      if (patientId) {
        dispatch(fetchPatientList({ forceRefresh: true }));
        eventLog("환자등록", { patientId });

        // 성공 토스트 메시지 표시
        ToastManager.show({
          content: "새 환자가 생성되었습니다.",
          duration: 3000,
        });

        navigate(`/patients/${patientId}`, { replace: true, state: { edit: true } });
      } else {
        throw new Error("환자 정보를 저장하는 데 실패했습니다.");
      }
    } catch (error) {
      log(LogLevel.ERROR, "handleCreatePatient error", error);
      ToastManager.show({
        content: error instanceof Error ? error.message : "환자를 생성하는 데 실패했습니다.",
        duration: 3000,
      });
    } finally {
      WaitingManager.hide();
      setIsModalOpen(false);
    }
  };

  return (
    <>
      <DashboardTemplate
        onPatientSelect={handlePatientSelect}
        onSelectReport={handleReportSelect}
        onAddPatient={handleOpenModal}
      />
      <AddPatientModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleCreatePatient}
      />
    </>
  );
};

// 대시보드는 로그인, 약관 동의, 가이드 동의가 필요
export default withPageGuard(Dashboard, {
  requireAuth: true,
  requireAgreement: true,
  requireGuide: true,
});
